import { useAuth } from "@/hooks/useAuth";
import { useDashboards } from "@/hooks/useDashboards";
import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@simplicity/ui";
import { HelpCircle, Send, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface HelpDashboard {
  id: string;
  title: string;
}

interface HelpPanelProps {
  userName: string;
  tenantName: string;
  dashboardName: string;
  setDashboardName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  dashboards: HelpDashboard[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

function HelpPanelHeader({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
      <h3 className="text-sm font-semibold">Submit a Help Request</h3>
      <Button
        variant="ghost"
        size="icon"
        onClick={onClose}
        className="text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

function HelpPanel(props: HelpPanelProps) {
  return (
    <div className="fixed bottom-4 right-4 z-100 w-[360px] max-w-[calc(100vw-2rem)] bg-background border border-border rounded-lg shadow-lg flex flex-col">
      <HelpPanelHeader onClose={props.onClose} />
      <div className="p-4 space-y-3 overflow-y-auto max-h-[60vh]">
        <div>
          <Label
            htmlFor="help-name"
            className="text-xs font-medium text-muted-foreground mb-1 block"
          >
            Your Name
          </Label>
          <Input
            id="help-name"
            value={props.userName}
            readOnly
            className="bg-muted cursor-not-allowed opacity-70"
          />
        </div>
        <div>
          <Label
            htmlFor="help-client"
            className="text-xs font-medium text-muted-foreground mb-1 block"
          >
            Client
          </Label>
          <Input
            id="help-client"
            value={props.tenantName}
            readOnly
            className="bg-muted cursor-not-allowed opacity-70"
          />
        </div>
        <div>
          <Label
            htmlFor="help-dashboard"
            className="text-xs font-medium text-muted-foreground mb-1 block"
          >
            Dashboard
          </Label>
          <Select value={props.dashboardName || undefined} onValueChange={props.setDashboardName}>
            <SelectTrigger id="help-dashboard">
              <SelectValue placeholder="Select a dashboard (optional)" />
            </SelectTrigger>
            <SelectContent>
              {props.dashboards.map((d) => (
                <SelectItem key={d.id} value={d.title}>
                  {d.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label
            htmlFor="help-description"
            className="text-xs font-medium text-muted-foreground mb-1 block"
          >
            Describe the issue
          </Label>
          <Textarea
            id="help-description"
            value={props.description}
            onChange={(e) => props.setDescription(e.target.value)}
            placeholder="What do you need help with?"
            rows={4}
            className="resize-none"
          />
        </div>
      </div>
      <div className="px-4 py-3 border-t border-border">
        <Button
          onClick={props.onSubmit}
          disabled={props.submitting || !props.description.trim()}
          className="w-full"
        >
          <Send className="h-4 w-4" />
          {props.submitting ? "Submitting..." : "Submit"}
        </Button>
      </div>
    </div>
  );
}

interface HelpFormProps {
  /** Controlled open state. When provided, internal state is bypassed. */
  open?: boolean;
  /** Notified on open/close (both controlled and uncontrolled modes). */
  onOpenChange?: (open: boolean) => void;
  /**
   * SIM-1816: hide the default icon-button trigger so the form can be opened
   * from an external control (e.g. the header user menu's "Help request" item).
   */
  hideTrigger?: boolean;
}

const HelpForm = ({ open: controlledOpen, onOpenChange, hideTrigger }: HelpFormProps = {}) => {
  const { user } = useAuth();
  const { currentTenantId, currentTenantName } = useTenant();
  const { data: dashboards = [] } = useDashboards();

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [tenantName, setTenantName] = useState("");
  const [dashboardName, setDashboardName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const userName = user?.email ?? "";

  // Auto-fill tenant name
  useEffect(() => {
    setTenantName(currentTenantName ?? "");
  }, [currentTenantName]);

  const handleSubmit = async () => {
    if (!description.trim() || !user || !currentTenantId) return;
    setSubmitting(true);
    try {
      await portalFetch(`/api/portal/tenants/${currentTenantId}/help`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantId: currentTenantId,
          userName,
          tenantName,
          dashboardName,
          description: description.trim(),
        }),
      });
      toast.success("Help request submitted!");
      setDescription("");
      setDashboardName("");
      setOpen(false);
    } catch {
      toast.error("Failed to submit help request.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <>
      {!hideTrigger && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setOpen(!open)}
          className="text-white hover:text-white/80"
          aria-label="Help"
        >
          <HelpCircle className="h-5 w-5" aria-hidden="true" />
        </Button>
      )}
      {open && (
        <HelpPanel
          userName={userName}
          tenantName={tenantName}
          dashboardName={dashboardName}
          setDashboardName={setDashboardName}
          description={description}
          setDescription={setDescription}
          dashboards={dashboards}
          submitting={submitting}
          onClose={() => setOpen(false)}
          onSubmit={() => void handleSubmit()}
        />
      )}
    </>
  );
};

export default HelpForm;
